<template>
  <div>
    <Popover
      v-model="desktopOpen"
      class="h-full w-full"
      hide-on-mobile
      no-padding
      custom-action-handler
    >
      <template #action>
        <button
          class="date-option__button"
          :class="buttonClasses"
          @click="toggleOpen"
          @keydown.space="toggleOpen"
        >
          <TagIcon
            :icon="DateIcon"
            color="mono-black"
            class="mr-2 sm:hidden w-5 h-5"
          />
          {{ label }}
          <TagIcon
            :icon="ChevronDownIcon"
            color="mono-black"
            class="ml-1 w-4 h-4"
          />
        </button>
      </template>

      <div>
        <DatePopup
          style="width: 604px"
          :form="form"
          :is-mobile="isMobile"
          @change="change"
        />

        <ClearAndSubmit
          :submit-label="submitLabel"
          :is-loading="isLoading"
          @clear="clear"
          @submit="submit"
        />
      </div>
    </Popover>

    <MobilePopover
      v-if="mobileOpen"
      title="Date and time"
      lock-background-scroll
      data-test-id="date-search-filter--mobile"
      @close="submit"
    >
      <DatePopup
        :form="form"
        :is-mobile="isMobile"
        @change="change"
      />

      <template #actions>
        <ClearAndSubmit
          :submit-label="submitLabel"
          :is-loading="isLoading"
          @clear="clear"
          @submit="submit"
        />
      </template>
    </MobilePopover>
  </div>
</template>

<script>
import ChevronDownIcon from '@tagvenue/icons/24/bold/arrows/chevron-down.svg';
import DateIcon from '@tagvenue/icons/24/line/time/calendar.svg';
import { markRaw } from 'vue';

import TagIcon from '@/components/TagIcon.vue';
import MobilePopover from '@/components/Ui/MobilePopover/MobilePopover.vue';
import Popover from '@/components/Ui/Popover.vue';
import ClearAndSubmit from '@/modules/public/search/components/SearchFilters/components/ClearAndSubmit.vue';
import { timeSlotsMaxValue } from '@/services/date';
import { getTimeSlots } from '@/services/hours-map.js';

import SearchOptionShared from '../SearchOptionShared.js';
import DatePopup from './DatePopup.vue';

export default {
  name: 'DateOption',
  mixins: [SearchOptionShared],
  components: { ClearAndSubmit, DatePopup, Popover, MobilePopover, TagIcon },
  data() {
    return {
      DateIcon: markRaw(DateIcon),
      ChevronDownIcon: markRaw(ChevronDownIcon),
    };
  },
  computed: {
    form() {
      return {
        timeFrom: this.$store.state.filters.timeFrom.value,
        timeTo: this.$store.state.filters.timeTo.value,
        date: this.$store.state.filters.date.value,
      };
    },
    timeSlots() {
      return getTimeSlots(this.$store.state.l10n.timeFormat);
    },
    label() {
      if (!this.form.date && !this.form.timeFrom && !this.form.timeTo) {
        return 'Date';
      }

      let localDate = window.dayjs(this.form.date, 'DD/MM/YYYY');
      localDate = localDate.isValid() ? localDate.format(this.$store.state.l10n.datepickerFormat) : '';

      if (this.isMobile) {
        if (this.form.date) {
          return localDate;
        }
      }
      let result = localDate || '';

      if (this.form.timeFrom && this.form.timeTo) {
        result += ` ${this.timeSlots[this.form.timeFrom]} - ${this.timeSlots[this.form.timeTo]}`;
      }
      if (!this.form.timeFrom && this.form.timeTo) {
        result += ` to ${this.timeSlots[this.form.timeTo]}`;
      }
      if (this.form.timeFrom && !this.form.timeTo) {
        result += ` from ${this.timeSlots[this.form.timeFrom]}`;
      }
      return result;
    },
    isSelected() {
      return this.form.date || this.form.timeFrom || this.form.timeTo;
    },
  },
  methods: {
    clear() {
      this.$store.commit('timeChange', [null, null]);
      this.$store.commit('onDateChange', null);
      this.apply({ name: 'RemoveFilter', value: 'date' });
    },
    change(key, value) {
      if (key === 'timeFrom') {
        const timeTo = Math.max(value + 2, this.form.timeTo);
        const time = [value, Math.min(timeTo, timeSlotsMaxValue)];
        this.$store.commit('timeChange', time);
        this.apply({ name: 'Time', value: time });
      }
      if (key === 'timeTo') {
        const time = [this.form.timeFrom, value];
        this.$store.commit('timeChange', time);
        this.apply({ name: 'Time', value: time });
      }
      if (key === 'date') {
        this.$store.commit('onDateChange', value);
        this.apply({ name: 'Date', value });
      }
    },
  },
  mounted() {
    window.addEventListener('scroll', this.closeOnScrollThrottled);

    if (this.$store.state.triggerSection) {
      return;
    }
    this.$nextTick(() => {
      if (this.$store.state.eventCategory === 'production' && !this.form.date) {
        this.toggleOpen();
      }
    });
  },
  unmounted() {
    window.removeEventListener('scroll', this.closeOnScrollThrottled);
  },
};
</script>

<style lang="scss" scoped>
@use '../optionButton';
@use "@sass/base/variables";

.option__button {
  @include optionButton.optionButton;
}

.date-option__button {
  justify-content: start;

  @media (min-width: variables.$screen-im) {
    justify-content: center;
  }
}

</style>
